// §4 S2 — Work order detail, /production/work-orders/:id (Module 04, 04-A).
//
// THE TAB STRIP IS Operations / Materials / Events / Genealogy / Costs /
// Calibration.
// EVENTS ARRIVED 2026-08-08 — the tab the §4 head-note recorded as the third
// instance of the reachable-on-the-API-only class: `operation_events` has
// existed since 04-B and §5 e10 has served the timeline since 04-B-2, so under
// the §4 tab rule the tab was OVERDUE, not premature. It rides e10's own
// permission fence, `production.read`, mirrored client-side exactly as
// Calibration mirrors e21's: absent without the grant, never present-and-403.
// GENEALOGY ARRIVED WITH 04-C-3 (2026-08-06): its table
// (`operation_serial_lot_captures`) exists, so the §4 tab rule admits it, and
// e3 serves its whole content additively. It carries no money, so unlike Costs
// and Calibration it rides no permission fence — every caller who may read the
// work order may read its lineage.
// Calibration ARRIVED with 04-C-2 (its table exists), fenced to
// `production.telemetry.read` — e21's own gate. The Costs tab additionally
// rides KEY PRESENCE: it is offered only when the detail payload carries the
// `costs` key (§7 — redaction is absence, and an absent key means this caller
// may not see money).
//
// HEADER TRANSITIONS are legal-state × permission, and every one of them lives
// inside `.wo-transitions`, which production.css REMOVES below `md` and
// replaces with the stated hint (§4 "Mobile behavior": office screens are
// responsive read-only on a phone).
//
// The §4 S2 row's "worker avatars" are AMENDED to 04-B in this change: no
// worker or labor data exists on a 04-A operation (§3.11's office path writes
// no labor entries by design), so an avatar would have nothing true to show.

import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { CardSkeleton, ErrorState } from "../../../../components/states";
import { StatusChip } from "../../../../components/status-chip";
import { Tabs } from "../../../../components/tabs";
import { UnauthorizedError } from "../../../../lib/problem";
import { requestIdOf } from "../../../../lib/request";
import { hasLiteralRole } from "../../../../lib/role-codes";
import { useTerm } from "../../../onboarding/hooks/terminology";
import { CreateReleaseModal } from "../components/create-release-modal";
import { CalibrationTab } from "../components/calibration-tab";
import { CloseBlockedNote } from "../components/close-blocked-note";
import { CostsTab } from "../components/costs-tab";
import { EventsTab } from "../components/events-tab";
import { GenealogyTab } from "../components/genealogy-tab";
import { MaterialsTab } from "../components/materials-tab";
import { OperationsTab } from "../components/operations-tab";
import { PlannedOrderRef, SalesOrderRef } from "../components/order-refs";
import { WorkOrderAdjustments } from "../../inventory/components/work-order-adjustments";
import {
  useCancelWorkOrder,
  useCloseWorkOrder,
  useCompleteWorkOrder,
  usePatchWorkOrder,
  useScrapReasons,
  useTravelerDownload,
  useWorkOrder,
  type WorkOrder,
  type WorkOrderCancelRequest,
  type WorkOrderPatchRequest,
} from "../hooks/production-queries";
import { useProductionPermissions } from "../hooks/permissions";
import {
  closeBlockersOf,
  formatBusinessDate,
  formatQty,
  formatTimestamp,
  isValidQtyInput,
  isWorkOrderOverdue,
  localeLabel,
  productionChipVariant,
  productionErrorMessage,
  qtyIsNonZero,
  serverDetailOf,
} from "../lib/format";

type TabKey = "operations" | "materials" | "events" | "genealogy" | "costs" | "calibration";

export function WorkOrderDetailScreen() {
  const { t, i18n } = useTranslation();
  const { term } = useTerm();
  const navigate = useNavigate();
  const params: Record<string, unknown> = useParams({ strict: false });
  const id = typeof params["id"] === "string" ? params["id"] : "";

  const perms = useProductionPermissions();
  const detail = useWorkOrder(id);

  const [tab, setTab] = useState<TabKey>("operations");
  const [panel, setPanel] = useState<"edit" | "cancel" | undefined>(undefined);
  const [releasing, setReleasing] = useState(false);

  const complete = useCompleteWorkOrder();
  const close = useCloseWorkOrder();
  const traveler = useTravelerDownload();

  useEffect(() => {
    if (detail.error instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [detail.error, navigate]);

  // ONE skeleton gate for BOTH feeds the strip is derived from — the detail
  // payload (Costs rides its `costs` key) and the permission mirror (Events
  // and Calibration ride their fences). The mirror is a fetch on first page
  // load (`/v1/auth/tenants`, hooks/permissions.ts), and 2026-08-08's staging
  // red caught the strip rendering in the window where the detail had settled
  // and the mirror had not: fail-closed `has()` said false, the strip painted
  // WITHOUT Events/Calibration, and both popped in ~80 ms later — layout
  // shift a planner mid-tap pays for. §4's rule is that tab PRESENCE is
  // decided by permission/key facts known at first render — never by fetch
  // timing — so the screen holds its skeleton until both feeds have settled
  // and the strip renders ONCE, complete. `isResolved` is true after a feed
  // ERROR too (isFetched), so a failed feed degrades to the stated fail-closed
  // absence rather than an eternal skeleton. Tabs whose CONTENT is still
  // loading (Events' e10 page) render their loading state INSIDE the panel.
  if (detail.isPending || !perms.isResolved) {
    return <CardSkeleton lines={6} label={t("production.common.loading")} />;
  }

  if (detail.error) {
    if (detail.error instanceof UnauthorizedError) return null;
    return (
      <>
        <ErrorState
          title={t("production.detail.error")}
          detail={detail.error instanceof Error ? detail.error.message : undefined}
          retryLabel={t("production.common.retry")}
          onRetry={() => void detail.refetch()}
          requestId={requestIdOf(detail.error)}
          requestIdLabel={t("production.common.requestId")}
        />
        <Link to="/production/work-orders">{t("production.detail.backToList")}</Link>
      </>
    );
  }

  const data = detail.data;
  const wo = data.work_order;
  const costsPresent = data.costs !== undefined;
  // 04-C-2: the tab rides e21's own permission fence, mirrored client-side.
  const calibrationPresent = perms.has("production.telemetry.read");
  // The Events tab rides e10's own fence the same way. Every §7 role that can
  // open this screen holds `production.read`, but the mirror is stated rather
  // than assumed: a custom role resolves to NO codes client-side
  // (hooks/permissions.ts — fail-closed, the server still decides), and a tab
  // that would 403 is worse than an absent one.
  const eventsPresent = perms.has("production.read");
  const activeTab: TabKey =
    (tab === "costs" && !costsPresent) ||
    (tab === "calibration" && !calibrationPresent) ||
    (tab === "events" && !eventsPresent)
      ? "operations"
      : tab;

  // LITERAL-ROLE-CODES: `work-orders.service.ts` grants the admin scope to the literal fa/ga codes.
  const isAdminScope = hasLiteralRole(perms, ["facility_admin", "group_admin"]);
  const materialIssued = data.material_lines.some((line) => qtyIsNonZero(line.qty_issued));

  // §4's header rule — legal-state × permission — applies to EVERY mutating
  // control on this screen, not only the transitions (spec §4 S2 note, added
  // 2026-08-14: the UX-3 sweep found the per-op Skip / Record result offered
  // on a CANCELLED order, every click a 409). A terminal order offers no
  // per-op action at all; each gate below mirrors ITS OWN endpoint's WO-status
  // arm rather than a shared guess.
  const woTerminal = wo.status === "cancelled" || wo.status === "closed";
  const woActive = wo.status === "released" || wo.status === "in_progress";
  // e9: refused only on `closed` server-side; skipping a DRAFT's step is
  // §3.2's own example (tailoring a routing before release). Cancelled is the
  // §3.1 truth rather than e9's current arm — a dead order's routing takes no
  // tailoring, and the spec note records the server-side gap.
  const canSkipOps = perms.has("production.operation.record") && !woTerminal;
  // e34's own predicate, verbatim: results post only against a released or
  // in-progress order — a draft or completed one offers no form that can
  // only 409.
  const canRecordResult = perms.has("production.operation.record") && woActive;
  // e32 refuses only `closed`; cancelled is a STATED narrowing (spec §4 S2
  // note) — module 07 does not exist to set a hold, and the sole release
  // control on a dead order invites an audit-chained override that unblocks
  // nothing.
  const canReleaseHold = perms.has("production.qc_hold.release") && !woTerminal;

  const canEdit =
    perms.has("production.wo.write") && (wo.status === "draft" || wo.status === "released");
  const canRelease = perms.has("production.wo.release") && wo.status === "draft";
  const canComplete = perms.has("production.wo.complete") && wo.status === "in_progress";
  const canClose = perms.has("production.wo.close") && wo.status === "completed";
  // §5 row 7: any pre-closed state; planner narrowed to draft/released
  // service-side — the UI mirrors the narrowing (hooks/permissions.ts note).
  const cancelLegal =
    wo.status === "draft" ||
    wo.status === "released" ||
    wo.status === "in_progress" ||
    wo.status === "completed";
  const canCancel =
    perms.has("production.wo.cancel") &&
    cancelLegal &&
    (wo.status === "draft" || wo.status === "released" || isAdminScope);
  // Traveler exists from release on (§5 row 29; a draft has none — 409).
  const canTraveler =
    wo.status === "released" ||
    wo.status === "in_progress" ||
    wo.status === "completed" ||
    wo.status === "closed";

  const transitionError = complete.error ?? close.error ?? traveler.error;
  // D-299 R10: a refused close is worded from the refusal's DATA — every
  // blocker, in the reader's language, with the way out or who holds it.
  const closeBlockers = closeBlockersOf(transitionError);

  const tabs: readonly TabKey[] = [
    "operations",
    "materials",
    // 2026-08-08: e10's timeline, fenced by its own `production.read` (§4 S2
    // "Events"; the third tab-arrival gap the §4 head-note records — closed).
    ...(eventsPresent ? (["events"] as const) : []),
    // 04-C-3: no fence and no key check — `serial_lot_captures` is always
    // served on e3, and its EMPTY state is designed (it distinguishes "no
    // capture yet" from "this order asks for none"), so the tab is present
    // whenever the payload is.
    "genealogy",
    ...(costsPresent ? (["costs"] as const) : []),
    ...(calibrationPresent ? (["calibration"] as const) : []),
  ];

  return (
    <section className="wo-detail">
      <div className="wo-header">
        <h1>
          {term("work_order")} {wo.wo_no}
        </h1>
        {/* `className="status-chip"` is the STABLE HOOK six certified specs
            read (`.wo-header .status-chip`, `op-row-N .status-chip`); the
            tinting is the §3.5 primitive's, keyed off the one status→variant
            map in lib/format.ts. */}
        <StatusChip variant={productionChipVariant(wo.status)} className="status-chip">
          {t(`production.status.wo.${wo.status}`)}
        </StatusChip>
      </div>

      <div className="wo-meta">
        <span>
          {t("production.detail.meta.due")}:{" "}
          {wo.due_date === null ? (
            t("production.common.notAvailable")
          ) : isWorkOrderOverdue(wo) ? (
            <span className="due-overdue">{formatBusinessDate(wo.due_date, i18n.language)}</span>
          ) : (
            formatBusinessDate(wo.due_date, i18n.language)
          )}
        </span>
        <span>{t("production.common.priorityLabel", { value: wo.priority })}</span>
        <span>
          {t("production.detail.meta.quantity")}:{" "}
          {t("production.list.progressOf", {
            good: formatQty(wo.qty_good, { locale: i18n.language }),
            planned: formatQty(wo.qty_planned, { locale: i18n.language }),
          })}{" "}
          {wo.uom}
        </span>
        <span>
          {t("production.detail.meta.created")}: {formatTimestamp(wo.created_at, i18n.language)}
        </span>
        {wo.notes !== null && wo.notes.length > 0 ? (
          <span>
            {t("production.detail.meta.notes")}: {wo.notes}
          </span>
        ) : null}
        {wo.status === "cancelled" && wo.cancel_reason !== null ? (
          <span>
            {t("production.detail.meta.cancelReason")}: {wo.cancel_reason}
          </span>
        ) : null}
      </div>

      {wo.planned_order_id !== null || wo.sales_order_line !== undefined ? (
        // P3-H-3 (D-391 ruling 3): where the order came from and whom it is
        // made for, each linked where the reader may open it.
        <div className="wo-refs" data-testid="wo-refs">
          {wo.planned_order_id !== null ? (
            <PlannedOrderRef plannedOrderId={wo.planned_order_id} facilityId={wo.facility_id} />
          ) : null}
          {wo.sales_order_line !== undefined ? (
            <span>
              {t("production.detail.meta.salesOrder")}:{" "}
              <SalesOrderRef value={wo.sales_order_line} />
            </span>
          ) : null}
        </div>
      ) : null}

      <div className="wo-transitions">
        {canEdit ? (
          // THE BUTTON NAMES WHAT IT OPENS (surface review F14). "Edit draft"
          // was shown on a RELEASED order, where §5 row 8 allows exactly one
          // field — priority — and the panel it opens contains exactly that
          // one field. A planner reading "Edit draft" on a released order
          // either believes the order is still a draft or believes the
          // quantity is about to become editable; both are false, and the
          // status chip two lines above says `released`.
          <Button
            data-testid="wo-edit-open"
            onClick={() => {
              setPanel(panel === "edit" ? undefined : "edit");
            }}
          >
            {wo.status === "draft"
              ? t("production.detail.action.edit")
              : t("production.detail.action.editPriority")}
          </Button>
        ) : null}
        {/* The one primary per view: release / complete / close are mutually
            exclusive by status, so at most one of the three renders. */}
        {canRelease ? (
          <Button
            variant="primary"
            onClick={() => {
              setReleasing(true);
            }}
          >
            {t("production.detail.action.release")}
          </Button>
        ) : null}
        {canComplete ? (
          <Button
            variant="primary"
            busy={complete.isPending}
            onClick={() => {
              complete.mutate(wo.id);
            }}
          >
            {t("production.detail.action.complete")}
          </Button>
        ) : null}
        {canClose ? (
          <Button
            variant="primary"
            busy={close.isPending}
            onClick={() => {
              close.mutate(wo.id);
            }}
          >
            {t("production.detail.action.close")}
          </Button>
        ) : null}
        {canCancel ? (
          <Button
            onClick={() => {
              setPanel(panel === "cancel" ? undefined : "cancel");
            }}
          >
            {t("production.detail.action.cancel")}
          </Button>
        ) : null}
        {canTraveler ? (
          <Button
            busy={traveler.isPending}
            onClick={() => {
              traveler.mutate(wo.id, {
                onSuccess: (blob) => {
                  // The catalogue qr.pdf pattern: hand the blob to the browser.
                  const url = URL.createObjectURL(blob);
                  window.open(url, "_blank", "noopener");
                  setTimeout(() => {
                    URL.revokeObjectURL(url);
                  }, 60_000);
                },
              });
            }}
          >
            {t("production.detail.action.traveler")}
          </Button>
        ) : null}
      </div>
      <p className="production-mobile-hint">{t("production.detail.action.mobileHint")}</p>

      {transitionError ? (
        <div className="form-error" role="alert" data-testid="wo-transition-error">
          <p>
            {productionErrorMessage(t, transitionError)}
            {serverDetailOf(t, transitionError) !== undefined ? (
              <span className="production-hint"> {serverDetailOf(t, transitionError)}</span>
            ) : null}
          </p>
          {closeBlockers !== undefined ? (
            <CloseBlockedNote blockers={closeBlockers} facilityId={wo.facility_id} />
          ) : null}
        </div>
      ) : null}

      {panel === "edit" ? (
        <EditPanel
          workOrder={wo}
          onDone={() => {
            setPanel(undefined);
          }}
        />
      ) : null}

      {panel === "cancel" ? (
        <CancelPanel
          workOrder={wo}
          materialIssued={materialIssued}
          onDone={() => {
            setPanel(undefined);
          }}
        />
      ) : null}

      {releasing ? (
        <CreateReleaseModal
          releaseWorkOrder={wo}
          onClose={() => {
            setReleasing(false);
          }}
        />
      ) : null}

      {/* THE §3.10 `Tabs` PRIMITIVE — real ARIA tabs (roving tabindex, arrow
          keys), which is what this strip has always been and what the M04
          responsive net reads: it discovers the strip with `getByRole("tab")`
          and asserts `aria-selected` flips. Same roles, same names, same
          permission-gated render-once behaviour (the screen still holds one
          skeleton until BOTH deciding feeds settle, above) — the hand-rolled
          `.tabs`/`.tab` markup and its stylesheet rules are gone, and the
          strip's deterministic 36 px height now comes from the primitive, so
          a permission-decided tab can no longer shift the page when it
          resolves (§5). */}
      <Tabs
        tabs={tabs.map((key) => ({ id: key, label: t(`production.detail.tab.${key}`) }))}
        active={activeTab}
        onSelect={(id) => {
          setTab(id as TabKey);
        }}
        label={t("production.detail.tabsAria")}
      />

      {activeTab === "operations" ? (
        <OperationsTab
          workOrderId={wo.id}
          facilityId={wo.facility_id}
          operations={data.operations}
          qtyReports={data.qty_reports}
          captures={data.serial_lot_captures}
          defaultUom={wo.uom}
          canSkip={canSkipOps}
          canRecord={canRecordResult}
          // e32's OWN code (04-C-5). `qc` holds this and NOT
          // `production.operation.record`, so folding the release button into
          // the record gate hid it from the one role §7 names for the decision.
          canReleaseHold={canReleaseHold}
        />
      ) : null}
      {activeTab === "materials" ? (
        <MaterialsTab
          workOrderId={wo.id}
          lines={data.material_lines}
          issues={data.material_issues}
          operations={data.operations}
          // e30's own arms, verbatim, since D-308: an issue moves material
          // against a released or in-progress order, and a return against a
          // released, in-progress or COMPLETED one — a completed order takes
          // leftover material back until it is closed. On any other status
          // either control could only 409 (spec §4 S2 note, 2026-08-14).
          canIssue={perms.has("production.material.issue") && woActive}
          canReturn={
            perms.has("production.material.issue") && (woActive || wo.status === "completed")
          }
          returnsOnly={wo.status === "completed"}
          // A closed order takes nothing back, so the tab names the way on for
          // leftover material instead (the D-308 review).
          closedToReturns={perms.has("production.material.issue") && wo.status === "closed"}
          currency={wo.currency}
          facilityId={wo.facility_id}
        />
      ) : null}
      {activeTab === "events" ? (
        <EventsTab workOrderId={wo.id} facilityId={wo.facility_id} operations={data.operations} />
      ) : null}
      {activeTab === "genealogy" ? (
        <GenealogyTab captures={data.serial_lot_captures} operations={data.operations} />
      ) : null}
      {activeTab === "calibration" ? <CalibrationTab workOrderId={wo.id} /> : null}
      {activeTab === "costs" && data.costs !== undefined ? (
        <CostsTab
          workOrderId={wo.id}
          costs={data.costs}
          outputs={data.outputs}
          // e12's guard is §9.4 close-immutability ALONE, so only `closed`
          // hides the opener — a cancelled order's write-off stays
          // finance-adjustable, deliberately (spec §4 S2 note, 2026-08-14).
          canAdjust={perms.has("production.cost.adjust") && wo.status !== "closed"}
        />
      ) : null}

      {/* P3-H-3 (D-342 ruling 2, D-391 ruling 3): module 06's movements and
          requests recorded against this order, under module 06's read code. */}
      <WorkOrderAdjustments workOrderId={wo.id} />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Edit (PATCH, §5 row 8): a draft edits everything; from `released` only
// priority is legal — the same fact the server enforces (WO_NOT_EDITABLE).
// ---------------------------------------------------------------------------

function EditPanel({ workOrder, onDone }: { workOrder: WorkOrder; onDone: () => void }) {
  const { t } = useTranslation();
  const patch = usePatchWorkOrder();
  const draftOnly = workOrder.status === "draft";

  const [qty, setQty] = useState(workOrder.qty_planned);
  const [dueDate, setDueDate] = useState(workOrder.due_date ?? "");
  const [priority, setPriority] = useState(String(workOrder.priority));
  const [notes, setNotes] = useState(workOrder.notes ?? "");
  const [validationKey, setValidationKey] = useState<string | undefined>(undefined);

  const title = draftOnly
    ? t("production.detail.editForm.title")
    : t("production.detail.editForm.titlePriority");

  return (
    <form
      className="production-panel"
      aria-label={title}
      onSubmit={(event) => {
        event.preventDefault();
        const body: WorkOrderPatchRequest = { priority: Number(priority) };
        if (draftOnly) {
          if (!isValidQtyInput(qty, { positive: true })) {
            setValidationKey("production.createModal.qtyInvalid");
            return;
          }
          body.qty_planned = qty.trim();
          body.due_date = dueDate.length > 0 ? dueDate : null;
          body.notes = notes.trim().length > 0 ? notes.trim() : null;
        }
        setValidationKey(undefined);
        patch.mutate({ id: workOrder.id, body }, { onSuccess: onDone });
      }}
    >
      <h3>{title}</h3>
      {draftOnly ? (
        <>
          <label className="filter-chip">
            <span className="filter-chip-label">{t("production.detail.editForm.qtyPlanned")}</span>
            <input
              value={qty}
              inputMode="decimal"
              onChange={(event) => {
                setQty(event.target.value);
              }}
            />
          </label>
          <label className="filter-chip">
            <span className="filter-chip-label">{t("production.detail.editForm.dueDate")}</span>
            <input
              type="date"
              value={dueDate}
              onChange={(event) => {
                setDueDate(event.target.value);
              }}
            />
          </label>
          <label className="filter-chip">
            <span className="filter-chip-label">{t("production.detail.editForm.notes")}</span>
            <textarea
              value={notes}
              onChange={(event) => {
                setNotes(event.target.value);
              }}
            />
          </label>
        </>
      ) : null}
      <label className="filter-chip">
        <span className="filter-chip-label">{t("production.detail.editForm.priority")}</span>
        <select
          value={priority}
          onChange={(event) => {
            setPriority(event.target.value);
          }}
        >
          {["1", "2", "3", "4", "5"].map((value) => (
            <option key={value} value={value}>
              {t("production.common.priorityLabel", { value })}
            </option>
          ))}
        </select>
      </label>
      <p className="production-hint">{t("production.detail.editForm.priorityHint")}</p>
      {validationKey !== undefined ? (
        <p className="form-error" role="alert">
          {t(validationKey)}
        </p>
      ) : null}
      {patch.isError ? (
        <p className="form-error" role="alert">
          {productionErrorMessage(t, patch.error)}
        </p>
      ) : null}
      <div className="form-actions">
        {/* type="submit" explicitly: Button defaults to type="button", and this
            is the control that submits the panel's <form>. */}
        <Button
          type="submit"
          variant="primary"
          busy={patch.isPending}
          busyLabel={t("production.common.saving")}
        >
          {t("production.common.save")}
        </Button>
        <Button onClick={onDone}>{t("production.common.cancel")}</Button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Cancel (§5 row 7). From `in_progress` the disposition sub-form answers the
// §3.1 guard in the same call. Once material is issued, a released/in_progress
// cancel is UN-CLEARABLE in 04-A (returns are 04-B's, D-076): the note states
// it and the server's 409 is surfaced — the button is not hidden.
// ---------------------------------------------------------------------------

function CancelPanel({
  workOrder,
  materialIssued,
  onDone,
}: {
  workOrder: WorkOrder;
  materialIssued: boolean;
  onDone: () => void;
}) {
  const { t, i18n } = useTranslation();
  const cancel = useCancelWorkOrder();
  const reasons = useScrapReasons();

  const [reason, setReason] = useState("");
  const [receiveQty, setReceiveQty] = useState("");
  const [scrapQty, setScrapQty] = useState("");
  const [scrapReason, setScrapReason] = useState("");

  const midRun = workOrder.status === "in_progress";

  return (
    <form
      className="production-panel"
      aria-label={t("production.detail.cancelForm.title")}
      onSubmit={(event) => {
        event.preventDefault();
        if (reason.trim().length === 0) return;
        const body: WorkOrderCancelRequest = { reason: reason.trim() };
        if (midRun) {
          const disposition: NonNullable<WorkOrderCancelRequest["partial_disposition"]> = {};
          if (receiveQty.trim().length > 0) disposition.receive_to_stock_qty = receiveQty.trim();
          if (scrapQty.trim().length > 0) disposition.scrap_qty = scrapQty.trim();
          if (scrapReason.length > 0) disposition.scrap_reason_code = scrapReason;
          if (Object.keys(disposition).length > 0) body.partial_disposition = disposition;
        }
        cancel.mutate({ id: workOrder.id, body }, { onSuccess: onDone });
      }}
    >
      <h3>{t("production.detail.cancelForm.title")}</h3>
      <label className="filter-chip">
        <span className="filter-chip-label">{t("production.detail.cancelForm.reason")}</span>
        <textarea
          value={reason}
          required
          onChange={(event) => {
            setReason(event.target.value);
          }}
        />
      </label>
      {midRun ? (
        <>
          <p>{t("production.detail.cancelForm.dispositionTitle")}</p>
          <p className="production-hint">{t("production.detail.cancelForm.dispositionHint")}</p>
          <label className="filter-chip">
            <span className="filter-chip-label">
              {t("production.detail.cancelForm.receiveQty")}
            </span>
            <input
              value={receiveQty}
              inputMode="decimal"
              onChange={(event) => {
                setReceiveQty(event.target.value);
              }}
            />
          </label>
          <label className="filter-chip">
            <span className="filter-chip-label">{t("production.detail.cancelForm.scrapQty")}</span>
            <input
              value={scrapQty}
              inputMode="decimal"
              onChange={(event) => {
                setScrapQty(event.target.value);
              }}
            />
          </label>
          <label className="filter-chip">
            <span className="filter-chip-label">
              {t("production.detail.cancelForm.scrapReason")}
            </span>
            <select
              value={scrapReason}
              onChange={(event) => {
                setScrapReason(event.target.value);
              }}
            >
              <option value="">{t("production.detail.ops.pickReason")}</option>
              {(reasons.data ?? []).map((cause) => (
                <option key={cause.id} value={cause.code}>
                  {localeLabel(cause.name_i18n, i18n.language)}
                </option>
              ))}
            </select>
          </label>
        </>
      ) : null}
      {materialIssued && workOrder.status !== "draft" ? (
        <p className="production-note" role="note">
          {t("production.detail.cancelForm.issuedBlockedNote")}
        </p>
      ) : null}
      {cancel.isError ? (
        <p className="form-error" role="alert">
          {productionErrorMessage(t, cancel.error)}
        </p>
      ) : null}
      <div className="form-actions">
        <Button type="submit" variant="primary" busy={cancel.isPending}>
          {t("production.detail.cancelForm.confirm")}
        </Button>
        <Button onClick={onDone}>{t("production.common.back")}</Button>
      </div>
    </form>
  );
}
